// Pure helpers for the Branch Cash screen - kept out of the component so they are unit-tested without rendering.
// The server re-validates everything; these only stop an obviously incomplete form before the round trip.

export const minutesToHHMM = (m: number): string =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** 'HH:MM' (00:00-11:59, the server's rollover limit) to minutes after midnight, or null when it is not a valid rollover time. */
export function hhmmToMinutes(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return Number(m[1]) < 12 && Number(m[2]) < 60 ? mins : null;
}

export const isMoney = (v: string): boolean => /^\d+(\.\d{1,2})?$/.test(v.trim());

export interface OpeningDay {
  openingBasis: 'CARRIED_FORWARD' | 'ESTABLISHED'; expectedOpening: number | null;
}

/** What the person typing an opening count should be told before they save (mirrors the server's classification). */
export function openingCountHint(day: OpeningDay, amount: string, thresholdRupees: number): { variance: number | null; needsReason: boolean; needsApproval: boolean } {
  if (day.openingBasis === 'ESTABLISHED' || day.expectedOpening === null) return { variance: null, needsReason: true, needsApproval: false };
  if (!isMoney(amount)) return { variance: null, needsReason: false, needsApproval: false };
  const variance = Math.round(Number(amount) * 100 - day.expectedOpening * 100) / 100;
  if (variance === 0) return { variance, needsReason: false, needsApproval: false };
  return { variance, needsReason: true, needsApproval: Math.abs(variance) >= thresholdRupees };
}

export function openingCountError(day: OpeningDay, amount: string, reason: string, sourceRef = ''): string {
  if (!isMoney(amount)) return 'Enter the counted cash as a rupee amount (up to 2 decimals)';
  const hint = openingCountHint(day, amount, 0);
  if (hint.needsReason && !reason.trim()) {
    return day.openingBasis === 'ESTABLISHED' ? 'Give a reason for establishing the opening balance' : 'The count differs from the carried-forward balance - explain the difference';
  }
  // A first opening is verified by a second person against a document, so it must say which document.
  if (day.openingBasis === 'ESTABLISHED' && !sourceRef.trim()) return 'Enter the source reference (the cash book page, handover slip or other document this balance comes from)';
  return '';
}

export const STATUS_LABEL: Record<string, string> = {
  AWAITING_COUNT: 'Awaiting opening count', PENDING_APPROVAL: 'Difference awaiting approval', VERIFIED: 'Opening verified',
  OPEN: 'Open', REOPENED: 'Reopened', CLOSED: 'Closed',
};

/** Plain-language names for the ledger's movement types (server registry: cashMovementTypes.ts). */
export const MOVEMENT_LABEL: Record<string, string> = {
  SALES_CASH_IN: 'Cash sale receipt', SALES_CASH_REVERSAL: 'Sale receipt reversed',
  ADVANCE_CASH_IN: 'Cash advance received', ADVANCE_CASH_REVERSAL: 'Advance reversed',
  EXPENSE_PAYMENT: 'Expense paid', EXPENSE_REVERSAL: 'Expense reversed', RECEIPT_IN: 'Cash receipt', RECEIPT_REVERSAL: 'Receipt reversed',
  TRANSFER_DISPATCH: 'Transfer sent', TRANSFER_RECEIPT: 'Transfer received', ADJUSTMENT_IN: 'Adjustment (in)', ADJUSTMENT_OUT: 'Adjustment (out)',
  SUPPLIER_PAYMENT: 'Supplier paid in cash', SUPPLIER_PAYMENT_REVERSAL: 'Supplier payment reversed',
};
export const movementLabel = (t: string): string => MOVEMENT_LABEL[t] ?? t;

/** Why a reversal-review decision cannot be submitted yet ('' when it can). Mirrors the server: a reason always, evidence for a cash return. */
export function reviewDecisionError(decision: string, reason: string, evidenceRef: string): string {
  if (decision !== 'CASH_RETURNED' && decision !== 'BOOK_ONLY') return 'Choose a decision';
  if (!reason.trim()) return 'A reason is required';
  if (decision === 'CASH_RETURNED' && !evidenceRef.trim()) return 'Evidence is required to record that cash was returned';
  return '';
}

/** How a closing count will be routed (mirrors the server): any difference needs a reason; one at/over the threshold needs another person's approval. */
export function closingCountHint(expected: number | null, counted: string, thresholdRupees: number): { variance: number | null; needsReason: boolean; needsApproval: boolean } {
  if (expected === null || !isMoney(counted)) return { variance: null, needsReason: false, needsApproval: false };
  const variance = Math.round(Number(counted) * 100) - Math.round(expected * 100);
  return { variance: variance / 100, needsReason: variance !== 0, needsApproval: variance !== 0 && Math.abs(variance) >= Math.round(thresholdRupees * 100) };
}

export function closingCountError(expected: number | null, counted: string, reason: string, thresholdRupees: number): string {
  if (!isMoney(counted)) return 'Enter the counted cash in rupees (up to 2 decimals)';
  if (closingCountHint(expected, counted, thresholdRupees).needsReason && !reason.trim()) return 'The count differs from the expected cash - explain the difference';
  return '';
}

export const CLOSING_STATUS_LABEL: Record<string, string> = {
  COUNTED: 'Counted', PENDING_APPROVAL: 'Awaiting approval', APPROVED: 'Approved', REJECTED: 'Rejected', CLOSED: 'Closed', SUPERSEDED: 'Superseded',
};
export const ADJUSTMENT_STATUS_LABEL: Record<string, string> = { PENDING_APPROVAL: 'Awaiting approval', POSTED: 'Posted', REJECTED: 'Rejected', CANCELLED: 'Cancelled' };
